import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Link,
    Stack,
    Divider,
    Card,
    Tooltip,
} from '@mui/material';
import ChevronRight from '@mui/icons-material/ChevronRight';

import { ChatMessage, useChat } from '../../../contexts/ChatContext';
import SmallLogo from '../../../components/logo/inbriefFullWhiteR_small.svg';
import { CHAT_FONT } from '@/constants/styles';
import { getDeterministicColor } from '@/utils/utils';
import Avatar from '@/components/@extended/Avatar';
import useAuth from '@/hooks/useAuth';
import ClipCard from '@/pages/custom-components/clipCard';
import { UI_ACTION_TYPE, UIFilterAction } from '@/types/chatTypes';
import { useFullscreenChat } from '@/contexts/FullscreenContext';
import { useDimensionCardProvider } from '@/contexts/application/DimensionCardsProvider';
import {useRightDrawer} from "@/contexts/application/RightDrawerContext";

interface MessageBubbleProps {
    message: ChatMessage;
    lastMessages: boolean;
}

const MAX_CHOICE_LABEL_LENGTH = 46;
const MAX_LENGTH = 80;
const MAX_MEDIA_ITEMS = 10;

const formatChoiceLabel = (text: string): string => {
    if (text.length <= MAX_CHOICE_LABEL_LENGTH) return text;
    return `${text.slice(0, MAX_CHOICE_LABEL_LENGTH - 1).trimEnd()}…`;
};

export const MessageBubble: React.FC<MessageBubbleProps> = ({ message, lastMessages }) => {
    const isUser = message.type === 'user';
    const { user } = useAuth();
    const {closeDrawer} = useRightDrawer()
    const {
        isFullscreenChat,
        activeChoices,
        sendConversationalChatMessage,
        executeDelayedMessagesActions,
    } = useFullscreenChat();
    const { sendChatMessage } = useChat();
    const { dimensionCard } = useDimensionCardProvider();

    const sendMsg = isFullscreenChat
        ? sendConversationalChatMessage
        : sendChatMessage;

    // Scelte disponibili nel messaggio corrente.
    const isCurrentActiveChoices =
        message.type === 'agent' &&
        !!message.choices &&
        activeChoices === message.choices;
    const [selections, setSelections] = useState<string[]>([]);

    // Espansione / riduzione del testo.
    const [isExpanded, setIsExpanded] = useState(lastMessages ?? false);
    const shouldCollapse = message.content.length > MAX_LENGTH && !lastMessages;
    const displayText = isExpanded
        ? message.content
        : message.content.length >= MAX_LENGTH
            ? `${message.content.slice(0, MAX_LENGTH)}...`
            : message.content.slice(0, MAX_LENGTH);

    // Media: al massimo 10 anteprime, seguite dalla card "More".
    const mediaItems = message.media?.slice(0, MAX_MEDIA_ITEMS) ?? [];
    const hasMedia = mediaItems.length > 0;
    type CarouselSlot = { clip?: (typeof mediaItems)[number]; isMore?: boolean };
    const carouselSlots: CarouselSlot[] = [
        ...mediaItems.map((clip) => ({ clip })),
        ...(hasMedia ? [{ isMore: true }] : []),
    ];

    // Azione associata ai risultati: permette di distinguere clip / key moments.
    const relevantAction = message.actions?.find(
        (action) =>
            action.type === UI_ACTION_TYPE.CREATE_CONTENT ||
            action.type === UI_ACTION_TYPE.FILTER,
    );

    let resourceType = 'clips';
    if (relevantAction?.type === UI_ACTION_TYPE.FILTER) {
        const params = relevantAction.params as UIFilterAction;
        resourceType = params?.resource_type?.toLowerCase().includes('clip')
            ? 'clips'
            : 'key_moments';
    }
    const moreLabel = resourceType === 'clips' ? 'More' : 'View in Library';

    useEffect(() => {
        setIsExpanded(lastMessages);
    }, [lastMessages]);

    useEffect(() => {
        if (isCurrentActiveChoices && message.choices) {
            setSelections(new Array(message.choices.length).fill(''));
        }
    }, [isCurrentActiveChoices, message.choices]);

    const toggleExpand = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        if (!lastMessages) setIsExpanded((prev) => !prev);
    };

    const handleSelectOption = (groupIdx: number, option: string) => {
        if (!message.choices) return;

        const updatedSelections = [...selections];
        updatedSelections[groupIdx] = option;
        setSelections(updatedSelections);

        if (updatedSelections.every((selection) => selection !== '')) {
            sendMsg(updatedSelections.join(', '));
        }
    };

    const handleMediaClick = () => {
        closeDrawer()
        executeDelayedMessagesActions(message.id);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                boxSizing: 'border-box',
                mb: 1,
            }}
        >
            {/* Header del messaggio */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    width: '100%',
                    px: 1,
                }}
            >
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: 30,
                            height: 30,
                            borderRadius: '50%',
                            flexShrink: 0,
                            backgroundColor: isUser ? 'primary.light' : 'transparent',
                            color: isUser ? 'primary.contrastText' : 'secondary.contrastText',
                        }}
                    >
                        {isUser ? (
                            <Avatar
                                sx={{
                                    bgcolor: getDeterministicColor(
                                        user?.name || '',
                                        user?.surname || '',
                                    ),
                                    color: 'white',
                                    width: 30,
                                    height: 30,
                                    fontSize: '1rem',
                                    fontWeight: 'bold',
                                }}
                            >
                                {user?.name?.[0]?.toUpperCase()}
                                {user?.surname?.[0]?.toUpperCase()}
                            </Avatar>
                        ) : (
                            <img src={SmallLogo} width="22px" height="22px" alt="AI" />
                        )}
                    </Box>
                </Box>

                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                    <Typography
                        variant="caption"
                        sx={{
                            fontWeight: 'bold',
                            opacity: 0.8,
                            color: 'primary.contrastText',
                            fontSize: CHAT_FONT,
                        }}
                    >
                        {isUser ? user?.name : 'InBrief AI Copilot'}
                    </Typography>
                </Box>

                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <Typography
                        variant="caption"
                        sx={{ opacity: 0.6, fontSize: '0.65rem', color: 'primary.contrastText' }}
                    >
                        {message.timestamp.toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                        })}
                    </Typography>
                </Box>
            </Box>

            <Divider sx={{ my: 1, borderColor: 'primary.contrastText', opacity: 0.3 }} />

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: isUser ? 'flex-end' : 'flex-start',
                    width: '100%',
                    pb: 1.5,
                    boxSizing: 'border-box',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isUser ? 'row-reverse' : 'row',
                        alignItems: 'flex-end',
                        gap: 1,
                        width: hasMedia ? '100%' : undefined,
                        maxWidth: hasMedia ? '100%' : '85%',
                        minWidth: 0,
                    }}
                >
                    {/* Bubble */}
                    <Paper
                        sx={{
                            p: 1.5,
                            backgroundColor: 'transparent',
                            backgroundImage: 'none',
                            border: '2px solid',
                            borderRadius: '12px',
                            borderColor: isUser ? 'primary.main' : 'secondary.main',
                            color: 'primary.contrastText',
                            width: hasMedia ? '100%' : undefined,
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Typography
                            sx={{
                                overflowWrap: 'anywhere',
                                whiteSpace: 'pre-wrap',
                                fontSize: CHAT_FONT,
                                color: 'primary.contrastText',
                            }}
                        >
                            {displayText}
                        </Typography>

                        {shouldCollapse && (
                            <Link
                                component="button"
                                variant="body2"
                                onClick={toggleExpand}
                                sx={{
                                    ml: 1,
                                    fontWeight: 'bold',
                                    fontSize: '0.8em',
                                    color: 'inherit',
                                    textDecoration: 'underline',
                                    opacity: 1,
                                    cursor: 'pointer',
                                    alignSelf: 'start',
                                    '&:hover': { opacity: 1 },
                                }}
                            >
                                {isExpanded ? 'Show Less' : 'Show More'}
                            </Link>
                        )}

                        {/* Scelte interattive */}
                        {isCurrentActiveChoices && message.choices && (
                            <Box sx={{ mt: 1.5 }}>
                                <Stack spacing={1.5} sx={{ width: '100%' }}>
                                    {message.choices.map((group, groupIdx) => (
                                        <Box key={groupIdx} sx={{ width: '100%' }}>
                                            {message.choices!.length > 1 && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        display: 'block',
                                                        color: 'secondary.contrastText',
                                                        mb: 0.5,
                                                        opacity: 0.8,
                                                    }}
                                                >
                                                    Option {groupIdx + 1} di {message.choices!.length}:
                                                </Typography>
                                            )}

                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    gap: 1,
                                                    width: '100%',
                                                }}
                                            >
                                                {group.map((option) => {
                                                    const isSelected = selections[groupIdx] === option;
                                                    const choiceLabel = formatChoiceLabel(option);
                                                    const isChoiceTruncated = choiceLabel !== option;

                                                    return (
                                                        <Tooltip
                                                            key={option}
                                                            title={isChoiceTruncated ? option : ''}
                                                            disableHoverListener={!isChoiceTruncated}
                                                            disableInteractive
                                                            placement="top"
                                                            arrow
                                                        >
                                                            <Button
                                                                onClick={() => handleSelectOption(groupIdx, option)}
                                                                disableElevation
                                                                sx={{
                                                                    flex: '0 1 auto',
                                                                    maxWidth: '100%',
                                                                    minWidth: 56,
                                                                    justifyContent: 'center',
                                                                    textTransform: 'none',
                                                                    whiteSpace: 'normal',
                                                                    overflowWrap: 'anywhere',
                                                                    py: 0.75,
                                                                    px: 1.75,
                                                                    borderRadius: '8px',
                                                                    fontSize: CHAT_FONT,
                                                                    lineHeight: 1.3,
                                                                    border: '1px solid',
                                                                    borderColor: isSelected
                                                                        ? 'primary.main'
                                                                        : 'rgba(255,255,255,0.24)',
                                                                    bgcolor: isSelected
                                                                        ? 'primary.main'
                                                                        : 'rgba(255,255,255,0.08)',
                                                                    color: 'primary.contrastText',
                                                                    '&:hover': {
                                                                        bgcolor: isSelected
                                                                            ? 'primary.dark'
                                                                            : 'rgba(255,255,255,0.16)',
                                                                        borderColor: isSelected
                                                                            ? 'primary.dark'
                                                                            : 'rgba(255,255,255,0.32)',
                                                                    },
                                                                }}
                                                            >
                                                                {choiceLabel}
                                                            </Button>
                                                        </Tooltip>
                                                    );
                                                })}
                                            </Box>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        )}

                        {/* Risultati media */}
                        {hasMedia && (
                            <Box sx={{ mt: 2, width: '100%', overflow: 'hidden' }}>
                                {/* Header dei risultati */}
                                <Box
                                    onClick={handleMediaClick}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        width: '100%',
                                        py: 0.25,
                                        mb: 1,
                                        '&:hover .MuiSvgIcon-root': { color: 'primary.main' },
                                    }}
                                >
                                    {relevantAction?.type === UI_ACTION_TYPE.FILTER ? (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                flexGrow: 1,
                                                gap: 1,
                                                minWidth: 0,
                                            }}
                                        >
                                            <Typography
                                                component="span"
                                                sx={{
                                                    fontSize: CHAT_FONT,
                                                    fontWeight: 400,
                                                    lineHeight: 1.4,
                                                    color: 'primary.contrastText',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                View More
                                            </Typography>
                                            <Box
                                                sx={{
                                                    flexGrow: 1,
                                                    height: '1px',
                                                    bgcolor: 'rgba(255,255,255,0.3)',
                                                }}
                                            />
                                        </Box>
                                    ) : (
                                        <Divider
                                            sx={{
                                                flexGrow: 1,
                                                borderColor: 'rgba(255,255,255,0.3)',
                                            }}
                                        />
                                    )}
                                    <ChevronRight sx={{ ml: 1, color: 'text.secondary' }} />
                                </Box>

                                {/* Carosello: una sola mappa e nessuno stretching verticale */}
                                <Box
                                    sx={{
                                        width: '100%',
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'flex-start',
                                        overflowX: 'auto',
                                        gap: 1.5,
                                        pb: 0.5,
                                    }}
                                >
                                    {carouselSlots.map((slot, index) => (
                                        <Box
                                            key={index}
                                            onClick={handleMediaClick}
                                            sx={{ flexShrink: 0, cursor: 'pointer' }}
                                        >
                                            {slot.isMore ? (
                                                <Card
                                                    sx={{
                                                        position: 'relative',
                                                        width: '120px',
                                                        minWidth: '120px',
                                                        height: `${160 * dimensionCard}px`,
                                                        borderRadius: 2,
                                                        overflow: 'hidden',
                                                        cursor: 'pointer',
                                                        bgcolor: 'rgba(255,255,255,0.05)',
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            inset: 0,
                                                            p: 2,
                                                            boxSizing: 'border-box',
                                                            borderTopLeftRadius: 8,
                                                            borderTopRightRadius: 8,
                                                            boxShadow: 3,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        <Typography variant="h6">
                                                            {moreLabel}
                                                        </Typography>
                                                    </Box>
                                                </Card>
                                            ) : (
                                                <ClipCard
                                                    card={slot.clip}
                                                    clipType={resourceType}
                                                    interactive={false}
                                                />
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* Azione presente, ma senza risultati */}
                        {!hasMedia && relevantAction && (
                            <Box
                                sx={{
                                    border: '3px dashed white',
                                    mx: '3px',
                                    width: '100%',
                                    aspectRatio: '16 / 9',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignSelf: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <Typography>No data Available</Typography>
                            </Box>
                        )}
                    </Paper>
                </Box>
            </Box>
        </Box>
    );
};